import "./index.scss";
import * as _ from "lodash";
import React, { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import Dropdown from "../../../../../components/core/dropdown";
import { useTranslation } from "react-i18next";
import {
  editProfile,
  readProfile,
} from "../../../../../store/profile/thunkAction";
import { getImageUrl } from "../../../../../utils/image";
import {
  sendNotifyIziMessageError,
  sendNotifyIziMessageSuccess,
} from "../../../../../utils/event";

const ProfileInitInfo = () => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const navigate = useNavigate();

  const params = useParams();
  const profileId = _.get(params, "id");

  useEffect(() => {
    dispatch(readProfile(profileId));
  }, [profileId, dispatch]);

  const profileManage = useSelector((state) => state.profileManage);
  const profile = _.get(profileManage, "profile");

  const [language, setLanguage] = useState("en");
  const [image, setImage] = useState("/img/chat/empty.jpg");
  const [domain, setDomain] = useState("");
  const [isLiveChat, setIsLiveChat] = useState(false);
  const [companyName, setCompanyName] = useState("");
  const [agentName, setAgentName] = useState("");
  const [name, setName] = useState("");
  const [file, setFile] = useState(null);

  useEffect(() => {
    setLanguage(profile.language || "en");
    setImage(profile.imageUrl || "/img/chat/empty.jpg");
    setDomain(profile.domain || "");
    setIsLiveChat(profile.isLiveChat || false);
    setCompanyName(profile.companyName || "");
    setAgentName(profile.agentName || "");
    setName(profile.name || "");
  }, [profile]);

  const optionsLanguage = [
    { name: t("common.language.vi"), id: "vi" },
    { name: t("common.language.en"), id: "en" },
  ];

  const handleSelectLanguage = (option) => {
    setLanguage(option?.id || "en");
  };

  const handleImageChange = (event) => {
    const file = event.target.files[0];
    if (file) {
      setFile(file);
      const reader = new FileReader();
      reader.onloadend = () => {
        setImage(reader.result);
      };
      reader.readAsDataURL(file);
    }
  };

  const validateProfile = () => {
    if (!name && !name.trim()) {
      sendNotifyIziMessageError(t("profile.name_is_require"));
      return false;
    }
    if (!domain && !domain.trim()) {
      sendNotifyIziMessageError(t("profile.domain_is_require"));
      return false;
    }
    if (!/^(?!:\/\/)([a-zA-Z0-9-]+\.)+[a-zA-Z]{2,}$/.test(domain)) {
      sendNotifyIziMessageError(t("profile.domain_invalid"));
      return false;
    }
    return true;
  };

  const saveProfile = async () => {
    const isOke = validateProfile();
    if (!isOke) {
      return;
    }
    const formData = new FormData();

    formData.append("domain", domain);
    formData.append("language", language);
    formData.append("isLiveChat", isLiveChat);
    formData.append("companyName", companyName);
    formData.append("agentName", agentName);
    formData.append("name", name);

    if (file) {
      formData.append("file", file);
    }

    try {
      await dispatch(
        editProfile({
          id: profileId,
          data: formData,
          isFormData: !!file,
        })
      ).unwrap();
      sendNotifyIziMessageSuccess(t("profile.profile_save_success"));
      navigate(`/profile/${profileId}/detail`);
    } catch (error) {
      const errorCode = _.get(error, "errorCode");
      if (errorCode === "FILE_NOT_ALLOWED") {
        sendNotifyIziMessageError(t("profile.upload_file_error"));
      } else if (errorCode === "FILE_TOO_LARGE") {
        sendNotifyIziMessageError(t("profile.file_too_large"));
      } else {
        sendNotifyIziMessageError(t("profile.profile_save_error"));
      }
    }
  };

  return (
    <div
      className="tab-pane fade active show"
      id="profiles-tab-pane"
      role="tabpanel"
      aria-labelledby="profiles-tab"
      tabIndex="0"
    >
      <div className="row mb-5">
        <div className="col-lg-3">
          <h5 className="title">{t("settings.profile.general")}</h5>
        </div>
        <div className="col-lg-7">
          <div className="row g-4">
            <div className="col-sm-5">
              <div className="d-flex gap-4">
                <img
                  className="avt-image"
                  id="imgAvt"
                  src={getImageUrl(image)}
                  alt=""
                />
                <div>
                  <div className="position-relative">
                    <input
                      type="file"
                      id="imgInp"
                      accept="image/*"
                      className="form-control opacity-0"
                      onChange={handleImageChange}
                    />
                    <span className="btn btn-info-light w-100 pe-none position-absolute top-0 start-0">
                      <i className="fa fa-upload"></i>{" "}
                      {t("settings.profile.upload_logo")}
                    </span>
                  </div>
                  <div className="mt-4">
                    - {t("settings.profile.logo_desc")}
                    <br />- {t("settings.profile.file_logo_allowed")}
                  </div>
                </div>
              </div>
            </div>
            <div className="col-12">
              <label className="form-label text-uppercase">
                {t("settings.profile.name")}
              </label>
              <input
                className="form-control"
                value={name}
                onChange={(e) => setName(e.target.value)}
              />
            </div>
            <div className="col-12">
              <label className="form-label text-uppercase">
                {t("settings.profile.domain")}
              </label>
              <input
                className="form-control"
                placeholder="domain.com.xyz"
                value={domain}
                onChange={(e) => setDomain(e.target.value)}
              />
            </div>
            <div className="col-sm-6">
              <label className="form-label text-uppercase">
                {t("settings.profile.language")}
              </label>
              <Dropdown
                config={{
                  options: optionsLanguage,
                  onSelect: handleSelectLanguage,
                  value: language,
                  className: "button-language",
                }}
              />
            </div>
            <div className="col-sm-6">
              <label className="form-label text-uppercase">
                {t("settings.profile.live_chat")}
              </label>
              <label className="form-check form-switch gap-2 d-flex align-items-center">
                <input
                  className="form-check-input"
                  type="checkbox"
                  role="switch"
                  checked={isLiveChat}
                  onChange={(e) => setIsLiveChat(e.target.checked)}
                />
                <span>
                  {isLiveChat ? t("common.enabled") : t("common.disabled")}
                </span>
              </label>
            </div>
            <div className="col-12">
              <label className="form-label text-uppercase">
                {t("settings.profile.company_name")}
              </label>
              <input
                className="form-control"
                value={companyName}
                onChange={(e) => setCompanyName(e.target.value)}
              />
            </div>
            <div className="col-12">
              <label className="form-label text-uppercase">
                {t("settings.profile.agent")}
              </label>
              <input
                className="form-control"
                value={agentName}
                onChange={(e) => setAgentName(e.target.value)}
              />
            </div>
          </div>
        </div>
      </div>

      <div className="card-footer">
        <div className="d-flex gap-3 justify-content-center align-items-center">
          <button onClick={() => saveProfile()} className="btn btn-primary">
            {t("common.save")} &amp; {t("common.exit")}
          </button>
          <button
            onClick={() => {
              navigate(`/profile/${profileId}/detail`);
            }}
            className="btn btn-gray btn-outline-gray"
          >
            {t("common.exit")}
          </button>
        </div>
      </div>
    </div>
  );
};

export default ProfileInitInfo;
